import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { type MobilityStockPublicListFragment } from '../../../../api/fragments';
import breakpoints from '../../../../utilities/breakpoints';
import useCompanyFormats from '../../../../utilities/useCompanyFormats';
import useTranslatedString from '../../../../utilities/useTranslatedString';
import VehicleImages from '../CarDetailsPage/VehicleImages';
import { type GiftVoucherKYCJourneyValues } from './KYCPage/shared';

const StyledDiv = styled.div`
    margin-bottom: 24px;
`;

const ImageContainer = styled.div`
    @media (max-width: ${breakpoints.sm}) {
        margin-left: -24px; // offset the margin of the parent container
        margin-right: -24px;
    }
`;

const VehicleName = styled.div`
    font-size: 28px;
    font-weight: bold;
`;

export type GiftVoucherVehicleInfoProps = {
    stock: MobilityStockPublicListFragment;
};

const GiftVoucherVehicleInfo = ({ stock }: GiftVoucherVehicleInfoProps) => {
    const { t } = useTranslation(['giftVoucherJourney']);
    const translated = useTranslatedString();

    const { values } = useFormikContext<GiftVoucherKYCJourneyValues>();
    const { formatAmountWithCurrency } = useCompanyFormats();

    const vehicleImages = useMemo(() => {
        if (stock.__typename !== 'MobilityStockInventory') {
            return null;
        }

        if (stock.inventory.__typename !== 'MobilityInventory') {
            return null;
        }

        if (stock.images && !isEmpty(stock.images)) {
            return stock.images.map(image => image.url);
        }

        if (stock.inventory.variant.images && !isEmpty(stock.inventory.variant.images)) {
            return stock.inventory.variant.images.map(image => image.url);
        }

        return [];
    }, [stock]);

    if (stock.__typename !== 'MobilityStockInventory') {
        return null;
    }

    if (stock.inventory.__typename !== 'MobilityInventory') {
        return null;
    }

    return (
        <>
            <ImageContainer>
                <VehicleImages sources={vehicleImages} />
            </ImageContainer>
            <StyledDiv>
                <VehicleName>{translated(stock.inventory.variant.name)}</VehicleName>
                {t('giftVoucherJourney:carPricePerDay', {
                    amount: formatAmountWithCurrency(values.value),
                })}
            </StyledDiv>
        </>
    );
};

export default GiftVoucherVehicleInfo;
